import { useState } from 'react'
import { NUDGGE_API_BASE_URL } from '../../lib/config'

const OTP_FUNCTIONS = [
  {
    name: 'OTP-Request-Email',
    description: 'Issue an OTP code and deliver it via the Email channel',
    method: 'POST',
    endpoint: '/functions/v1/OTP-Request-Email',
    features: [
      'Authentication via OTP API key',
      'Per-account rate limits (daily + per-user)',
      'Resend cooldown enforcement',
      'Delegates send to Email channel via EmailApiKey',
      'Optional template UUID for branded delivery',
      'Persists request in OTPRequests with expiry'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates OTP API key from X-Api-Key header' },
      { step: 2, title: 'Account Lookup', description: 'Resolves OTPAccounts row + downstream EmailApiKey' },
      { step: 3, title: 'Rate Limit Check', description: 'Enforces account-wide + per-user daily caps and resend cooldown' },
      { step: 4, title: 'Code Generation', description: 'Creates numeric code of DefaultLength (or caller override)' },
      { step: 5, title: 'Persist Request', description: 'Inserts into OTPRequests with Channel=email, Status=Pending, ExpiresUtc' },
      { step: 6, title: 'Dispatch Email', description: 'Calls Send-Email / Send-Email-Templated using EmailApiKey' },
      { step: 7, title: 'Response', description: 'Returns CodeId + expiry for client to collect code' }
    ]
  },
  {
    name: 'OTP-Request-WhatsApp',
    description: 'Issue an OTP code and deliver it via the WhatsApp channel',
    method: 'POST',
    endpoint: '/functions/v1/OTP-Request-WhatsApp',
    features: [
      'Authentication via OTP API key',
      'MSISDN normalization',
      'Per-account rate limits',
      'Delegates send to WhatsApp via WhatsAppApiKey',
      'Uses WhatsAppTemplateIdentifier for approved templates',
      'Persists request in OTPRequests'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates OTP API key from X-Api-Key header' },
      { step: 2, title: 'Account Lookup', description: 'Resolves OTPAccounts row + downstream WhatsAppApiKey' },
      { step: 3, title: 'MSISDN Normalize', description: 'Strips non-digits and canonicalizes the target phone number' },
      { step: 4, title: 'Rate Limit Check', description: 'Enforces account-wide + per-user daily caps and resend cooldown' },
      { step: 5, title: 'Code Generation', description: 'Creates numeric code of DefaultLength (or caller override)' },
      { step: 6, title: 'Persist Request', description: 'Inserts into OTPRequests with Channel=whatsapp, Status=Pending' },
      { step: 7, title: 'Dispatch WhatsApp', description: 'Calls Send-WhatsApp-Template with approved template' },
      { step: 8, title: 'Response', description: 'Returns CodeId + expiry for client' }
    ]
  },
  {
    name: 'OTP-Validate-Email',
    description: 'Validate an OTP code for a pending Email request',
    method: 'POST',
    endpoint: '/functions/v1/OTP-Validate-Email',
    features: [
      'Authentication via OTP API key',
      'CodeId-based lookup',
      'Expiry window enforcement',
      'Marks request as Validated with ValidatedUtc timestamp',
      'Returns validation outcome to caller'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates OTP API key' },
      { step: 2, title: 'Request Lookup', description: 'Finds OTPRequests row by CodeId + target email' },
      { step: 3, title: 'Expiry Check', description: 'Rejects if ExpiresUtc is in the past' },
      { step: 4, title: 'Code Comparison', description: 'Compares submitted code to stored Code' },
      { step: 5, title: 'State Transition', description: 'Updates Status=Validated, sets ValidatedUtc on success' },
      { step: 6, title: 'Response', description: 'Returns validation result' }
    ]
  },
  {
    name: 'OTP-Validate-WhatsApp',
    description: 'Validate an OTP code for a pending WhatsApp request',
    method: 'POST',
    endpoint: '/functions/v1/OTP-Validate-WhatsApp',
    features: [
      'Authentication via OTP API key',
      'CodeId-based lookup',
      'Expiry window enforcement',
      'Marks request as Validated with ValidatedUtc timestamp',
      'Optionally receives inbound WhatsApp message as proof'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates OTP API key' },
      { step: 2, title: 'Request Lookup', description: 'Finds OTPRequests row by CodeId + MSISDN' },
      { step: 3, title: 'Expiry Check', description: 'Rejects if ExpiresUtc is in the past' },
      { step: 4, title: 'Code Comparison', description: 'Compares submitted code to stored Code' },
      { step: 5, title: 'State Transition', description: 'Updates Status=Validated, sets ValidatedUtc on success' },
      { step: 6, title: 'Response', description: 'Returns validation result' }
    ]
  },
  {
    name: 'OTP-Is-Number-validated',
    description: 'Lightweight check: has a given target been validated recently?',
    method: 'POST',
    endpoint: '/functions/v1/OTP-Is-Number-validated',
    features: [
      'Authentication via OTP API key',
      'Takes an MSISDN or Email + optional lookback window',
      'Returns boolean validated + timestamp if found',
      'Useful for gating sensitive flows without re-issuing OTPs'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates OTP API key' },
      { step: 2, title: 'Target Resolution', description: 'Normalizes the MSISDN or email input' },
      { step: 3, title: 'Recent Validation Lookup', description: 'Searches OTPRequests for a validated row within the lookback window' },
      { step: 4, title: 'Response', description: 'Returns { validated: bool, validatedUtc?: timestamp }' }
    ]
  }
]

export default function EdgeFunctionsTab() {
  const [selectedFunction, setSelectedFunction] = useState(null)

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center space-x-3 mb-2">
          <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Edge Functions</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          OTP Supabase Edge Functions. The OTP service delegates actual delivery to the Email / SMS / WhatsApp channels using stored downstream API keys.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-3">
          {OTP_FUNCTIONS.map((func) => (
            <button
              key={func.name}
              onClick={() => setSelectedFunction(func)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedFunction?.name === func.name
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">{func.name}</h3>
                <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                  {func.method}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">{func.description}</p>
              <code className="text-xs text-purple-600 dark:text-purple-400 mt-2 block">{func.endpoint}</code>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selectedFunction ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 sm:p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{selectedFunction.name}</h3>
                    <p className="text-purple-100 mt-1 text-sm sm:text-base">{selectedFunction.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFunction(null)}
                    className="p-1 hover:bg-white/20 rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">{selectedFunction.method}</span>
                  <code className="px-3 py-1 bg-black/20 rounded-full text-sm font-mono">{selectedFunction.endpoint}</code>
                </div>
              </div>

              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Key Features</h4>
                <ul className="space-y-2">
                  {selectedFunction.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 sm:p-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Business Logic Flow</h4>
                <div className="space-y-3">
                  {selectedFunction.businessLogic.map((logic, idx) => (
                    <div
                      key={idx}
                      className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {logic.step}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">{logic.title}</h5>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{logic.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">API Example</h4>
                <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs">
                  <code className="text-gray-800 dark:text-gray-200">{`curl -X ${selectedFunction.method} \\
  '${NUDGGE_API_BASE_URL}${selectedFunction.endpoint}' \\
  -H 'Content-Type: application/json' \\
  -H 'X-Api-Key: YOUR_OTP_API_KEY' \\
  -d '{"msisdn": "+27821234567"}'`}</code>
                </pre>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Select a Function</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose an Edge Function from the list to view its implementation and documentation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
